import type { FunctionModuleTreeDTO, VisitCountDetailVo, VisitorCountDetailVo } from "@zhihaoscm/entity";
import dayjs from "dayjs";
import { useEffect, useState, type CSSProperties } from "react";
import {
    Button as ZhButton,
    Dialog as ZhDialog,
    DateRangePicker as ZhDateRangePicker,
    Input as ZhInput,
    Select as ZhSelect,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { dashboardService } from "@/api/services/dashboardService";
import { LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { dateFormat, dateTimeFormat } from "@/utils";
import { formatTimeEn } from "@/utils/formatter";
import { CLIENT_TYPE_USER } from "@zhihaoscm/meta";

export interface FunctionalDetailSearchParams {
    scope?: number;
    beginTime?: string;
    endTime?: string;
}

interface FunctionalDetailBaseProps {
    open: boolean;
    info: FunctionModuleTreeDTO;
    searchParams: FunctionalDetailSearchParams;
    onClose: () => void;
    canExport?: boolean;
}

/** 沿用旧 Vue `.module-name` 的弹窗模块名称样式，保持视觉层级和间距一致。 */
const functionalDetailModuleNameStyle: CSSProperties = {
    color: "#000000e0",
    fontFamily: "PingFang SC",
    fontSize: 14,
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "14px",
    marginBottom: 20
};

export type FunctionalDetailExport = (params: FunctionalDetailQuery, filename: string) => Promise<unknown>;

export interface FunctionalVisitCountDialogProps extends FunctionalDetailBaseProps {
    loadRows?: (params: FunctionalDetailQuery) => Promise<VisitorCountDetailVo[]>;
    exportRows?: FunctionalDetailExport;
}

export interface FunctionalVisitorCountDialogProps extends FunctionalDetailBaseProps {
    loadRows?: (params: FunctionalDetailQuery) => Promise<VisitCountDetailVo[]>;
    exportRows?: FunctionalDetailExport;
}

export interface FunctionalDetailQuery extends FunctionalDetailSearchParams {
    moduleName: string;
    page: number;
    size: number;
    keyword?: string;
    clientType?: number[];
    innerBeginTime?: string;
    innerEndTime?: string;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

/** 访问次数明细默认接口，沿用旧 Vue find-visitor-count-detail。 */
export async function loadFunctionalVisitCountDetail(params: FunctionalDetailQuery) {
    return dashboardService.findVisitorCountDetail(params as never);
}

/** 访问人数明细默认接口，沿用旧 Vue find-visit-count-detail。 */
export async function loadFunctionalVisitorCountDetail(params: FunctionalDetailQuery) {
    return dashboardService.findVisitCountDetail(params as never);
}

/** 访问次数明细导出，沿用旧 Vue 的接口和文件下载契约。 */
export function exportFunctionalVisitCountDetail(params: FunctionalDetailQuery, filename: string) {
    return dashboardService.exportVisitorCountDetail(params, filename);
}

/** 访问人数明细导出，沿用旧 Vue 的接口和文件下载契约。 */
export function exportFunctionalVisitorCountDetail(params: FunctionalDetailQuery, filename: string) {
    return dashboardService.exportFindVisitCountDetail(params, filename);
}

/** 组合模块名称、页面筛选和明细分页参数，保持 Vue 请求边界。 */
export function buildFunctionalDetailQuery(
    info: FunctionModuleTreeDTO,
    searchParams: FunctionalDetailSearchParams,
    keyword?: string,
    clientType?: number[],
    dateRange?: [string, string],
    sort?: { sortKey?: string; sortOrder?: "asc" | "desc" }
): FunctionalDetailQuery {
    const scopedQuery = searchParams.scope && searchParams.scope !== 6 ? { scope: searchParams.scope } : searchParams;
    const normalizedKeyword = keyword?.trim();
    const normalizedClientType = clientType?.length ? clientType : undefined;
    const hasDateRange = Boolean(dateRange?.[0] && dateRange?.[1]);
    const query = { ...scopedQuery };
    if (hasDateRange) {
        delete query.beginTime;
        delete query.endTime;
    }
    return {
        ...query,
        moduleName: info.moduleName,
        page: 1,
        size: 10,
        ...(normalizedKeyword ? { keyword: normalizedKeyword } : {}),
        ...(normalizedClientType ? { clientType: normalizedClientType } : {}),
        ...(hasDateRange
            ? {
                  innerBeginTime: dateFormat(dateRange?.[0], LOG_DATE_FORMAT_START),
                  innerEndTime: dateFormat(dateRange?.[1], LOG_DATE_FORMAT_END)
              }
            : {}),
        ...(sort?.sortKey ? { sortKey: sort.sortKey } : {}),
        ...(sort?.sortOrder ? { sortOrder: sort.sortOrder } : {})
    };
}

const text = (value: unknown) => (value == null || value === "" ? "--" : String(value));

/** 为两个明细表生成可复用行键，避免同一客户多条记录触发 React 重复 key。 */
export function getFunctionalDetailRowKey(row: VisitorCountDetailVo | VisitCountDetailVo) {
    if ("eventTime" in row) {
        return [row.customerInfo, row.eventTime, row.entranceName].map(value => value || "--").join("-");
    }
    return [row.customerInfo, row.clientTypes, row.stayTime, row.visitorCount, row.avgSingleStayTime]
        .map(value => value ?? "--")
        .join("-");
}

/** 将旧 Vue 终端枚举值转换为用户可读文案，保留已格式化字符串并兜底未知数字。 */
export function formatFunctionalClientType(value: unknown): string {
    if (value == null || value === "") return "--";
    if (typeof value === "number" || /^\d+$/.test(String(value))) {
        return CLIENT_TYPE_USER.options.find(option => String(option.value) === String(value))?.label || "--";
    }
    return String(value);
}

/** 将访问人数明细的多终端编码按旧 Vue 枚举转换，已格式化字符串保持原文。 */
export function formatFunctionalClientTypes(value: unknown): string {
    if (value == null || value === "") return "--";
    if (typeof value === "number") return formatFunctionalClientType(value);
    const rawValue = String(value);
    if (!/^\d+(\s*,\s*\d+)*$/.test(rawValue)) return rawValue;
    return rawValue
        .split(",")
        .map(item => formatFunctionalClientType(item.trim()))
        .join(",");
}

const visitColumns: TableColumnProps<VisitorCountDetailVo>[] = [
    { prop: "customerInfo", label: "手机号/个人实名", render: ({ row }) => text(row.customerInfo) },
    { prop: "clientType", label: "终端", width: 120, render: ({ row }) => formatFunctionalClientType(row.clientType) },
    { prop: "entranceName", label: "入口名称", render: ({ row }) => text(row.entranceName) },
    {
        prop: "eventTime",
        label: "点击时间",
        width: 180,
        sortable: "custom",
        render: ({ row }) => dateTimeFormat(row.eventTime)
    }
];

const visitorColumns: TableColumnProps<VisitCountDetailVo>[] = [
    { prop: "customerInfo", label: "手机号/个人实名", render: ({ row }) => text(row.customerInfo) },
    { prop: "clientTypes", label: "终端", render: ({ row }) => formatFunctionalClientTypes(row.clientTypes) },
    {
        prop: "stayTime",
        label: "停留时长",
        width: 130,
        sortable: "custom",
        render: ({ row }) => formatTimeEn(row.stayTime)
    },
    {
        prop: "visitorCount",
        label: "访问次数",
        width: 100,
        sortable: "custom",
        render: ({ row }) => text(row.visitorCount)
    },
    {
        prop: "avgSingleStayTime",
        label: "平均单次停留时长",
        width: 160,
        sortable: "custom",
        render: ({ row }) => formatTimeEn(row.avgSingleStayTime)
    }
];

/** 功能访问次数明细弹窗，沿用旧 Vue 840 宽度与模块标题。 */
export function FunctionalVisitCountDialog({
    open,
    info,
    searchParams,
    onClose,
    loadRows = loadFunctionalVisitCountDetail,
    exportRows = exportFunctionalVisitCountDetail,
    canExport = false
}: FunctionalVisitCountDialogProps) {
    const [rows, setRows] = useState<VisitorCountDetailVo[]>([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [keyword, setKeyword] = useState("");
    const [appliedKeyword, setAppliedKeyword] = useState<string>();
    const [clientTypes, setClientTypes] = useState<number[]>([]);
    const [appliedClientTypes, setAppliedClientTypes] = useState<number[]>([]);
    const [dateRange, setDateRange] = useState<[string, string]>(["", ""]);
    const [sort, setSort] = useState<{ sortKey?: string; sortOrder?: "asc" | "desc" }>();

    useEffect(() => {
        if (!open) return;
        let disposed = false;
        setLoading(true);
        void loadRows(
            buildFunctionalDetailQuery(info, searchParams, appliedKeyword, appliedClientTypes, dateRange, sort)
        )
            .then(result => {
                if (!disposed) setRows(result || []);
            })
            .catch(() => {
                if (!disposed) setRows([]);
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [appliedClientTypes, appliedKeyword, dateRange, info, loadRows, open, searchParams, sort]);

    /** 显式提交关键词，避免输入过程触发旧 Vue 表格请求。 */
    const search = () => {
        setAppliedKeyword(keyword.trim() || undefined);
        setAppliedClientTypes(clientTypes);
    };

    /** 重置旧 Vue 明细表的搜索、日期和排序草稿，恢复首屏查询。 */
    const reset = () => {
        setKeyword("");
        setAppliedKeyword(undefined);
        setClientTypes([]);
        setAppliedClientTypes([]);
        setDateRange(["", ""]);
        setSort(undefined);
    };

    /** 导出当前模块访问次数明细，保持旧 Vue 的模块名和时间戳文件名。 */
    const exportDetail = async () => {
        setExporting(true);
        try {
            await exportRows(
                buildFunctionalDetailQuery(info, searchParams, appliedKeyword, appliedClientTypes, dateRange, sort),
                `${info.moduleName}访问次数-${dayjs().format("YYYYMMDDHHmmss")}.xlsx`
            );
        } finally {
            setExporting(false);
        }
    };

    return (
        <ZhDialog open={open} title="访问次数明细" width={840} onCancel={onClose} footer={null}>
            <div className="functional-detail-module-name" style={functionalDetailModuleNameStyle}>
                {info.moduleName}(功能名称)
            </div>
            {canExport ? (
                <div className="mb-4">
                    <ZhButton type="primary" loading={exporting ? true : undefined} onClick={() => void exportDetail()}>
                        导出访问次数明细
                    </ZhButton>
                </div>
            ) : null}
            <div className="mb-4 flex gap-2">
                <ZhInput
                    aria-label="手机号/个人实名搜索"
                    placeholder="请输入手机号/个人实名"
                    value={keyword}
                    onChange={event => setKeyword(event.target.value)}
                />
                <ZhSelect
                    aria-label="终端筛选"
                    mode="multiple"
                    placeholder="请选择终端"
                    value={clientTypes}
                    options={CLIENT_TYPE_USER.options.map(option => ({
                        label: option.label,
                        value: Number(option.value)
                    }))}
                    onChange={value => setClientTypes((value as Array<string | number>).map(Number))}
                />
                <ZhDateRangePicker
                    aria-label="点击时间筛选"
                    type="daterange"
                    value={dateRange}
                    valueFormat="YYYY-MM-DD"
                    format="YYYY-MM-DD"
                    startPlaceholder="开始时间"
                    endPlaceholder="结束时间"
                    rangeSeparator="-"
                    onChange={(_value, formattedValue) => setDateRange(formattedValue || ["", ""])}
                />
                <ZhButton aria-label="搜索访问次数明细" type="primary" onClick={search}>
                    搜索
                </ZhButton>
                <ZhButton aria-label="重置访问次数明细" onClick={reset}>
                    重置
                </ZhButton>
            </div>
            <div aria-busy={loading} data-loading={String(loading)} data-testid="functional-detail-table">
                {loading ? (
                    <div className="py-4 text-center text-black/40" role="status" aria-label="功能明细加载中">
                        加载中...
                    </div>
                ) : null}
                <ZhTable<VisitorCountDetailVo>
                    data={rows}
                    columns={visitColumns}
                    rowKey={getFunctionalDetailRowKey}
                    maxHeight={460}
                    pagination={false}
                    onSortChange={sorter =>
                        setSort(
                            sorter.prop && sorter.order
                                ? {
                                      sortKey: sorter.prop,
                                      sortOrder: sorter.order === "ascending" ? "asc" : "desc"
                                  }
                                : undefined
                        )
                    }
                />
            </div>
        </ZhDialog>
    );
}

/** 功能访问人数明细弹窗，沿用旧 Vue 840 宽度与模块标题。 */
export function FunctionalVisitorCountDialog({
    open,
    info,
    searchParams,
    onClose,
    loadRows = loadFunctionalVisitorCountDetail,
    exportRows = exportFunctionalVisitorCountDetail,
    canExport = false
}: FunctionalVisitorCountDialogProps) {
    const [rows, setRows] = useState<VisitCountDetailVo[]>([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [keyword, setKeyword] = useState("");
    const [appliedKeyword, setAppliedKeyword] = useState<string>();
    const [clientTypes, setClientTypes] = useState<number[]>([]);
    const [appliedClientTypes, setAppliedClientTypes] = useState<number[]>([]);
    const [sort, setSort] = useState<{ sortKey?: string; sortOrder?: "asc" | "desc" }>();

    useEffect(() => {
        if (!open) return;
        let disposed = false;
        setLoading(true);
        void loadRows(
            buildFunctionalDetailQuery(info, searchParams, appliedKeyword, appliedClientTypes, undefined, sort)
        )
            .then(result => {
                if (!disposed) setRows(result || []);
            })
            .catch(() => {
                if (!disposed) setRows([]);
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [appliedClientTypes, appliedKeyword, info, loadRows, open, searchParams, sort]);

    /** 显式提交关键词，避免输入过程触发旧 Vue 表格请求。 */
    const search = () => {
        setAppliedKeyword(keyword.trim() || undefined);
        setAppliedClientTypes(clientTypes);
    };

    /** 重置旧 Vue 明细表的搜索和排序草稿，恢复首屏查询。 */
    const reset = () => {
        setKeyword("");
        setAppliedKeyword(undefined);
        setClientTypes([]);
        setAppliedClientTypes([]);
        setSort(undefined);
    };

    /** 导出当前模块访问人数明细，保持旧 Vue 的模块名和时间戳文件名。 */
    const exportDetail = async () => {
        setExporting(true);
        try {
            await exportRows(
                buildFunctionalDetailQuery(info, searchParams, appliedKeyword, appliedClientTypes, undefined, sort),
                `${info.moduleName}访问人数-${dayjs().format("YYYYMMDDHHmmss")}.xlsx`
            );
        } finally {
            setExporting(false);
        }
    };

    return (
        <ZhDialog open={open} title="访问人数明细" width={840} onCancel={onClose} footer={null}>
            <div className="functional-detail-module-name" style={functionalDetailModuleNameStyle}>
                {info.moduleName}(功能名称)
            </div>
            {canExport ? (
                <div className="mb-4">
                    <ZhButton type="primary" loading={exporting ? true : undefined} onClick={() => void exportDetail()}>
                        导出访问人数明细
                    </ZhButton>
                </div>
            ) : null}
            <div className="mb-4 flex gap-2">
                <ZhInput
                    aria-label="手机号/个人实名搜索"
                    placeholder="请输入手机号/个人实名"
                    value={keyword}
                    onChange={event => setKeyword(event.target.value)}
                />
                <ZhSelect
                    aria-label="终端筛选"
                    mode="multiple"
                    placeholder="请选择终端"
                    value={clientTypes}
                    options={CLIENT_TYPE_USER.options.map(option => ({
                        label: option.label,
                        value: Number(option.value)
                    }))}
                    onChange={value => setClientTypes((value as Array<string | number>).map(Number))}
                />
                <ZhButton aria-label="搜索访问人数明细" type="primary" onClick={search}>
                    搜索
                </ZhButton>
                <ZhButton aria-label="重置访问人数明细" onClick={reset}>
                    重置
                </ZhButton>
            </div>
            <div aria-busy={loading} data-loading={String(loading)} data-testid="functional-detail-table">
                {loading ? (
                    <div className="py-4 text-center text-black/40" role="status" aria-label="功能明细加载中">
                        加载中...
                    </div>
                ) : null}
                <ZhTable<VisitCountDetailVo>
                    data={rows}
                    columns={visitorColumns}
                    rowKey={getFunctionalDetailRowKey}
                    maxHeight={460}
                    pagination={false}
                    onSortChange={sorter =>
                        setSort(
                            sorter.prop && sorter.order
                                ? {
                                      sortKey: sorter.prop,
                                      sortOrder: sorter.order === "ascending" ? "asc" : "desc"
                                  }
                                : undefined
                        )
                    }
                />
            </div>
        </ZhDialog>
    );
}
